import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BuildingOffice2Icon,
  DocumentTextIcon,
  CogIcon,
  FolderIcon,
  CircleStackIcon,
  PuzzlePieceIcon,
  CheckBadgeIcon,
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
  TableCellsIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  MegaphoneIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import CustomCollectionsService from "@/services/CustomCollectionsServiceNew";
import AvailabilityService, { AVAILABILITY_REQUESTS_CHANGED } from "@/services/AvailabilityService";
import { COLLECTIONS_CHANGED_EVENT } from "@/lib/collectionsEvents";

const toId = (s = "") => s.toLowerCase().trim().replace(/\s+/g, "_");

const normalizeCollection = (col) => {
  const raw = col?.name || col?._id || col?.id || "";
  const slug = toId(raw);
  return {
    name: raw || slug || "Unnamed",
    collectionName: slug,
    _id: col?._id || col?.id || slug,
    visibleInMenu: !!col?.visibleInMenu,
  };
};

export function useNavigationGroups() {
  const { t } = useTranslation();
  const [collections, setCollections] = useState([]);
  const [loadingNav, setLoadingNav] = useState(false);
  const [navError, setNavError] = useState(null);
  const [availabilityRequestCount, setAvailabilityRequestCount] = useState(0);

  const fetchCollections = async (mountedRef = { current: true }) => {
    setLoadingNav(true);
    setNavError(null);
    const { data, error } = await CustomCollectionsService.getCustomCollectionsWithFilters({
      limit: 100,
      sortColumn: 'name',
      sortDirection: 'asc'
    });
    if (!mountedRef.current) return;
    setLoadingNav(false);
    if (error) {
      setNavError(error);
      setCollections([]);
      return;
    }
    const mapped = (Array.isArray(data) ? data : [])
      .filter((c) => !!c?.visibleInMenu)
      .map(normalizeCollection)
      .reduce((acc, c) => {
        if (!acc.some((x) => x.collectionName === c.collectionName))
          acc.push(c);
        return acc;
      }, [])
      .sort((a, b) => a.name.localeCompare(b.name));
    setCollections(mapped);
  };

  useEffect(() => {
    const mounted = { current: true };
    const refreshRequestCount = async () => {
      const result = await AvailabilityService.getRequests({ limit: 1 });
      if (!mounted.current || result.error) return;
      setAvailabilityRequestCount(result.totalCount || 0);
    };
    refreshRequestCount();
    window.addEventListener(AVAILABILITY_REQUESTS_CHANGED, refreshRequestCount);
    fetchCollections(mounted);

    const onChanged = () => fetchCollections(mounted);
    window.addEventListener(COLLECTIONS_CHANGED_EVENT, onChanged);

    return () => {
      mounted.current = false;
      window.removeEventListener(COLLECTIONS_CHANGED_EVENT, onChanged);
      window.removeEventListener(AVAILABILITY_REQUESTS_CHANGED, refreshRequestCount);
    };
  }, []);

  const navigationGroups = useMemo(
    () => [
      {
        name: t("sidebar.groups.clients"),
        items: [
          {
            name: t("sidebar.items.placowki"),
            href: "/klienci/placowki",
            icon: BuildingOffice2Icon,
            current: false,
          },
          {
            name: t("sidebar.items.wojewodztwa"),
            href: "/klienci/wojewodztwa",
            icon: BuildingOffice2Icon,
            current: false,
          },
          {
            name: t("sidebar.items.osoba"),
            href: "/klienci/osoba",
            icon: BuildingOffice2Icon,
            current: false,
          },
        ],
      },
      {
        name: t("sidebar.groups.organization"),
        collapsible: true,
        items: [
          {
            name: t("sidebar.items.miasto"),
            href: "/klienci/miasto",
            icon: BuildingOffice2Icon,
            current: false,
          },
          {
            name: t("sidebar.items.gmina"),
            href: "/klienci/gmina",
            icon: BuildingOffice2Icon,
            current: false,
          },
          {
            name: t("sidebar.items.powiat"),
            href: "/klienci/powiat",
            icon: BuildingOffice2Icon,
            current: false,
          },
          {
            name: t("sidebar.items.typyPlacowek"),
            href: "/klienci/typy-placowek",
            icon: BuildingOffice2Icon,
            current: false,
          },
        ],
      },
      {
        name: t("sidebar.groups.communication"),
        items: [
          {
            name: t("sidebar.items.rozmowy"),
            href: "/komunikacja/rozmowy",
            icon: ChatBubbleLeftRightIcon,
            current: false,
          },
          {
            name: t("sidebar.items.kampanie"),
            href: "/komunikacja/kampanie",
            icon: MegaphoneIcon,
            current: false,
          },
        ],
      },
      {
        name: t("sidebar.groups.accounting"),
        items: [
          {
            name: t("sidebar.items.invoices"),
            href: "/ksiegowosc/invoices",
            icon: DocumentTextIcon,
            current: false,
          },
          {
            name: t("sidebar.items.korekty"),
            href: "/ksiegowosc/korekty",
            icon: DocumentTextIcon,
            current: false,
          },
          {
            name: t("sidebar.items.rozliczenia"),
            href: "/ksiegowosc/rozliczenia",
            icon: ClipboardDocumentCheckIcon,
            current: false,
          },
          // {
          //   name: t("sidebar.items.expenses"),
          //   href: "/ksiegowosc/expenses",
          //   icon: FolderIcon,
          //   current: false,
          // },
        ],
      },
      {
        name: t("sidebar.groups.trainers"),
        items: [
          {
            name: t("sidebar.items.trainingAvailability"),
            href: "/centrumSzkolen/dyspozycja",
            icon: CalendarDaysIcon,
            current: false,
          },
          {
            name: t("sidebar.items.myProjects"),
            href: "/centrumSzkolen/moje-projekty",
            icon: ClipboardDocumentListIcon,
            current: false,
          },
        ],
      },
      {
        name: t("sidebar.groups.coordinators"),
        items: [
          {
            name: t("sidebar.items.projects"),
            href: "/centrumSzkolen/projekty",
            icon: FolderIcon,
            current: false,
          },
          {
            name: t("sidebar.items.trainingCertifications"),
            href: "/centrumSzkolen/certyfikaty",
            icon: CheckBadgeIcon,
            current: false,
          },
          {
            name: t("sidebar.items.virtualBoard"),
            href: "/centrumSzkolen/wirtualna-tablica",
            icon: TableCellsIcon,
            current: false,
          },
          {
            name: t("sidebar.items.availabilityChangeRequests"),
            href: "/centrumSzkolen/zmiany-dostepnosci",
            icon: ClipboardDocumentCheckIcon,
            badge: availabilityRequestCount,
            current: false,
          },
        ],
      },
      {
        name: t("sidebar.groups.customDatabases"),
        items: collections.map((c) => ({
          name: c.name,
          icon: PuzzlePieceIcon,
          href: `/single-custom-collection/${c._id}`,
          current: false,
        })),
      },
      {
        name: t("sidebar.groups.system"),
        collapsible: true,
        items: [
          {
            name: t("sidebar.items.uzytkownicy"),
            href: "/system/uzytkownicy",
            icon: UsersIcon,
            current: false,
          },
          {
            name: t("sidebar.items.customDatabases"),
            href: "/custom-collections",
            icon: CircleStackIcon,
            current: false,
          },
          {
            name: t("sidebar.items.settings"),
            href: "/settings",
            icon: CogIcon,
            current: false,
          },
        ],
      },
    ],
    [availabilityRequestCount, collections, t]
  );

  const navigation = useMemo(
    () => navigationGroups.flatMap((g) => g.items),
    [navigationGroups]
  );

  return { navigationGroups, navigation, loadingNav, navError };
}

export function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
